import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { CollectionSummary } from '@qkindex/contracts';

// Reuse immutable image objects across daily visits; bound disk use across accounts.
export const IMAGE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const IMAGE_CACHE_MAX_BYTES = 200 * 1024 * 1024;
interface CachedImage {
  blob: Blob;
  savedAt: number;
  accessedAt: number;
}
interface ImageCacheSchema extends DBSchema {
  images: { key: [string, string, string, string]; value: CachedImage };
}
let pending: Promise<unknown> = Promise.resolve();
let generation = 0;
const userGenerations = new Map<string, number>();
const collectionGenerations = new Map<string, number>();
const scopeKey = (userId: string, collectionId: string) =>
  JSON.stringify([userId, collectionId]);

export const imageCacheIdentity = (collection: CollectionSummary): string =>
  JSON.stringify([
    collection.membershipVersion,
    collection.role,
    collection.lifecycleVersion,
    collection.lifecycle,
    collection.ownerUserId,
  ]);

const enqueue = <T>(
  operation: (db: IDBPDatabase<ImageCacheSchema>) => Promise<T>,
): Promise<T | undefined> => {
  const result = pending
    .then(async () => {
      const db = await openDB<ImageCacheSchema>('qkindex-image-blobs-v1', 1, {
        upgrade(database) {
          database.createObjectStore('images');
        },
      });
      try {
        return await operation(db);
      } finally {
        db.close();
      }
    })
    .catch(() => undefined);
  pending = result;
  return result;
};

/** Only call after live inventory authority has confirmed this collection. */
export function createImageBlobCache(
  userId: string,
  collectionId: string,
  identity: string,
) {
  const session = generation;
  const user = userGenerations.get(userId) ?? 0;
  const scope = scopeKey(userId, collectionId);
  const collection = collectionGenerations.get(scope) ?? 0;
  const current = () =>
    session === generation &&
    user === (userGenerations.get(userId) ?? 0) &&
    collection === (collectionGenerations.get(scope) ?? 0);
  return {
    read(path: string) {
      return enqueue(async (db) => {
        if (!current()) return undefined;
        const key: ImageCacheSchema['images']['key'] = [
          userId,
          collectionId,
          identity,
          path,
        ];
        const tx = db.transaction('images', 'readwrite');
        void tx.done.catch(() => undefined);
        const saved = await tx.store.get(key);
        const now = Date.now();
        if (saved && now - saved.savedAt < IMAGE_CACHE_TTL_MS) {
          await tx.store.put({ ...saved, accessedAt: now }, key);
          await tx.done;
          return current() ? saved.blob : undefined;
        }
        if (saved) await tx.store.delete(key);
        await tx.done;
        return undefined;
      });
    },
    write(path: string, blob: Blob) {
      return enqueue(async (db) => {
        if (!current() || blob.size > IMAGE_CACHE_MAX_BYTES) return;
        const now = Date.now();
        const tx = db.transaction('images', 'readwrite');
        void tx.done.catch(() => undefined);
        const key: ImageCacheSchema['images']['key'] = [
          userId,
          collectionId,
          identity,
          path,
        ];
        await tx.store.put({ blob, savedAt: now, accessedAt: now }, key);
        // Remove expired entries and evict least recently used bytes in the same transaction.
        const entries: Array<{ key: typeof key; value: CachedImage }> = [];
        let bytes = 0;
        let cursor = await tx.store.openCursor();
        while (cursor) {
          if (now - cursor.value.savedAt >= IMAGE_CACHE_TTL_MS)
            await cursor.delete();
          else {
            entries.push({ key: cursor.key, value: cursor.value });
            bytes += cursor.value.blob.size;
          }
          cursor = await cursor.continue();
        }
        entries.sort((a, b) => a.value.accessedAt - b.value.accessedAt);
        for (const entry of entries) {
          if (bytes <= IMAGE_CACHE_MAX_BYTES) break;
          await tx.store.delete(entry.key);
          bytes -= entry.value.blob.size;
        }
        await tx.done;
      });
    },
  };
}

export function clearImageBlobCaches(
  userId?: string,
  collectionId?: string,
): Promise<void | undefined> {
  // Invalidate factories synchronously so late downloads cannot repopulate cleared data.
  if (userId === undefined) {
    generation += 1;
    userGenerations.clear();
    collectionGenerations.clear();
  } else if (collectionId === undefined) {
    userGenerations.set(userId, (userGenerations.get(userId) ?? 0) + 1);
  } else {
    const scope = scopeKey(userId, collectionId);
    collectionGenerations.set(
      scope,
      (collectionGenerations.get(scope) ?? 0) + 1,
    );
  }
  return enqueue(async (db) => {
    const tx = db.transaction('images', 'readwrite');
    if (userId === undefined) await tx.store.clear();
    else {
      let cursor = await tx.store.openCursor();
      while (cursor) {
        if (
          cursor.key[0] === userId &&
          (collectionId === undefined || cursor.key[1] === collectionId)
        )
          await cursor.delete();
        cursor = await cursor.continue();
      }
    }
    await tx.done;
  });
}
